import { isEmpty } from 'lodash';
import i18n from '@fiverr/i18n/singleton';
import pathfinder from '@fiverr-private/pathfinder';
import type { EagerJobsTbdSdk } from '@fiverr-private/jtbd_sdk';
import { Notification } from '../types/notifications';
import { Order } from '../types/gqlTypes';
import { getOrders } from './services/getOrders';
import { getUserNotifications } from './services/getUserNotifications';
import { getRelevantOrder } from './utils/getRelevantOrder';
import { DEFAULT_ICON_URL } from './constants';

const isValidResponse = (responses: [Order[], Notification[]]) => responses.some((arr) => !isEmpty(arr));
const TTL_7_DAYS = 7;

export const OrderUpdatesCardImpl = {
    getJobInfo: async ({ externalServicesInfo, context, jtbdDismissInfo }) => {
        const responses = await Promise.all([
            getOrders(externalServicesInfo.federationPegasus.url, context.userId as number),
            getUserNotifications(externalServicesInfo.pinnedFlashesChimera.url),
        ]);

        if (!isValidResponse(responses)) {
            return {
                shouldDisplayJob: false,
            };
        }

        const relevantOrder = getRelevantOrder(responses, jtbdDismissInfo);

        if (!relevantOrder) {
            return {
                shouldDisplayJob: false,
            };
        }

        const { type, encryptedId, seller, content, isRead, id: orderId, status, updatedAt } = relevantOrder;
        const translationParams = {
            $scope: context.locale,
            sellerName: seller.name,
            content,
        };

        return {
            shouldDisplayJob: true,
            dismissInfo: {
                isDismissible: true,
                jtbdTTL: TTL_7_DAYS,
                itemId: encryptedId,
                updatedAt,
            },
            headerText: i18n.t(`jobs_tbd.order_updates.header_text`, translationParams),
            jobPages: [
                {
                    iconUrl: seller.profileImageUrl || DEFAULT_ICON_URL,
                    title: i18n.t(`jobs_tbd.order_updates.title.${type}`, translationParams),
                    subtitle: i18n.t(`jobs_tbd.order_updates.subtitle.${type}`, translationParams),
                    ctaText: i18n.t(`jobs_tbd.order_updates.cta.${type}`, translationParams),
                    sellerData: {
                        username: seller.name,
                        hasAvatar: !!seller.profileImageUrl,
                    },
                    biEnrichment: {
                        order: {
                            id: parseInt(orderId, 10),
                            status,
                        },
                    },
                },
            ],
            isEmphasizedCard: !isRead,
            clickAction: {
                type: 'link',
                href: pathfinder('order_page_orders_order_id', {
                    order_id: encryptedId,
                }),
            },
        };
    },
} satisfies EagerJobsTbdSdk;
